/* Stockist's supported deep link: ?stockist-query=ZIP. */
(() => {
  if (customElements.get("tempters-locator-popup")) return;
  class LocatorPopup extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.design = Boolean(window.Shopify && window.Shopify.designMode);
      this.key = "tempters_store_locator_popup_v1_" + this.dataset.sectionId;
      this.dialog = this.querySelector('[role="dialog"]');
      this.querySelectorAll("[data-slp-close]")
      .forEach((button) => button.addEventListener("click", () => this.close(), options));
      const link = this.querySelector("[data-slp-locator-link]");
      if (link) link.addEventListener("click", () => this.remember(), options);
      const form = this.querySelector("form");
      if (form)
        form.addEventListener(
          "submit",
          (event) => {
            event.preventDefault();
            const input = form.querySelector("input");
            input.value = input.value.trim();
            if (!form.reportValidity()) return;
            const url = new URL(this.dataset.locatorUrl, location.origin);
            if (!["http:", "https:"].includes(url.protocol)) return;
            url.searchParams.set("stockist-query", input.value);
            // Preserve the selected Shopify preview theme across the navigation.
            const preview = new URL(location.href).searchParams.get("preview_theme_id");
            if (preview && url.origin === location.origin) url.searchParams.set("preview_theme_id", preview);
            if (this.design) return;
            this.remember();
            location.assign(url.href);
          },
          options,
        );
      document.addEventListener(
        "keydown",
        (event) => {
          if (this.hidden) return;
          if (event.key === "Escape") {
            event.preventDefault();
            this.close();
          }
          if (event.key === "Tab") {
            const targets = Array.from(this.dialog.querySelectorAll("button, a[href], input")).filter((el) => !el.disabled && el.getClientRects().length);
            const first = targets[0],
              last = targets[targets.length - 1];
            if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
        },
        options,
      );
      if (this.design) {
        if (this.dataset.editorPreview === "true") this.open("your state");
        document.addEventListener(
          "shopify:section:select",
          (event) => {
            if (event.detail.sectionId === this.dataset.sectionId) this.open("your state");
          },
          options,
        );
        document.addEventListener(
          "shopify:section:deselect",
          (event) => {
            if (event.detail.sectionId === this.dataset.sectionId) this.close(false);
          },
          options,
        );
      } else this.init();
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.timer);
      if (!this.hidden) this.close(false);
    }
    dismissed() {
      try {
        if (this.dataset.dismissMode === "always") return false;
        if (this.dataset.dismissMode === "session") return sessionStorage.getItem(this.key) === "1";
        return Number(localStorage.getItem(this.key)) > Date.now();
      } catch (_) {
        return false;
      }
    }
    remember() {
      if (this.design) return;
      try {
        if (this.dataset.dismissMode === "session") sessionStorage.setItem(this.key, "1");
        else if (this.dataset.dismissMode === "days") localStorage.setItem(this.key, String(Date.now() + Number(this.dataset.dismissDays || 7) * 86400000));
      } catch (_) {}
    }
    close(remember = true) {
      if (remember) this.remember();
      this.hidden = true;
      document.documentElement.classList.remove("store-locator-popup-open");
      if (this.previous?.isConnected) this.previous.focus();
    }
    open(state) {
      for (const [selector, template] of [
        ["[data-slp-heading]", this.dataset.headingTemplate],
        ["[data-slp-message]", this.dataset.messageTemplate],
      ]) {
        this.querySelector(selector).textContent = (template || "").replaceAll("{state}", state || "your state");
      }
      this.previous = document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add("store-locator-popup-open");
      (this.querySelector("input") || this.dialog).focus();
    }
    blocked() {
      // Let age verification and other active modal dialogs finish first.
      return (
        Array.from(document.querySelectorAll('[aria-modal="true"], dialog[open], [data-meta-age-gate]')).some((el) => {
          if (this.contains(el) || el.closest("[hidden]")) return false;
          const style = getComputedStyle(el);
          return style.visibility !== "hidden" && style.display !== "none" && el.getClientRects().length > 0;
        }) ||
        getComputedStyle(document.body).overflow === "hidden" ||
        getComputedStyle(document.documentElement).overflow === "hidden"
      );
    }
    async state() {
      let cached;
      try {
        cached = JSON.parse(sessionStorage.getItem("tempters_geo_state_v1"));
      } catch (_) {}
      if (cached) return cached;
      for (const endpoint of ["https://ipwho.is/", "https://ipapi.co/json/"]) {
        const controller = new AbortController();
        const abort = () => controller.abort();
        this.abort.signal.addEventListener("abort", abort, { once: true });
        const timeout = setTimeout(abort, 3500);
        try {
          const response = await fetch(endpoint, { signal: controller.signal, headers: { Accept: "application/json" } });
          if (!response.ok) continue;
          const data = await response.json();
          if (data.success === false) continue;
          const geo = { country_code: data.country_code || data.country, region_code: data.region_code || data.regionCode, region: data.region || data.region_name };
          if (!geo.country_code || !geo.region_code) continue;
          try {
            sessionStorage.setItem("tempters_geo_state_v1", JSON.stringify(geo));
          } catch (_) {}
          return geo;
        } catch (_) {
        } finally {
          clearTimeout(timeout);
          this.abort.signal.removeEventListener("abort", abort);
        }
      }
      return null;
    }
    async init() {
      if (this.dataset.enabled !== "true" || this.dismissed()) return;
      const target = new URL(this.dataset.locatorUrl, location.origin);
      // Never interrupt the results page or any pre-filled Stockist search.
      if (location.pathname.replace(/\/$/, "") === target.pathname.replace(/\/$/, "") || new URL(location.href).searchParams.has("stockist-query")) return;
      let state = "your state";
      if (this.dataset.audience === "restricted") {
        const geo = await this.state();
        const codes = (this.dataset.restrictedStates || "").split(",").map((code) => code.trim().toUpperCase());
        if (!geo || String(geo.country_code).toUpperCase() !== "US" || !codes.includes(String(geo.region_code).toUpperCase())) return;
        state = geo.region || geo.region_code;
      }
      if (!this.isConnected) return;
      const attempt = () => {
        if (!this.isConnected || this.dismissed()) return;
        if (this.blocked()) {
          this.timer = setTimeout(attempt, 500);
          return;
        }
        this.open(state);
      };
      this.timer = setTimeout(attempt, Math.max(0, Number(this.dataset.delay) || 0));
    }
  }
  customElements.define("tempters-locator-popup", LocatorPopup);
})();
